import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link } from "react-router-dom";
import { useRecoilState, useResetRecoilState } from "recoil";
import { useAtomValue } from "jotai";

import "./RequestDialog.scss";
import { Dialog } from "../../base";
import { ReactComponent as CloseIcon } from "../../../static/icons/close.svg";
import {
  REQUEST_SERVICE,
  requestErrorState,
  RequestForm,
  RequestService,
  requestState,
} from "../../../store/RequestStore";
import { useLocalStorage } from "../../../shared/utill";
import { useInjection } from "../../../shared/InversifyProvider";
import { totalKeywordAtom } from "../../../shared/KeywordAtom";
import { useLoading } from "../../../contexts/LoadingProvider";
import {
  getAreaListFromAirtable,
  isSoldOutState,
} from "../../../lib/airtable";
import {
  CONTACT_VALIDATION_ERROR_MESSAGES,
  getMobilePhoneCaretPosition,
  sanitizeContactNameInput,
  validateContactName,
  validateContactPhone,
} from "../../../utils/validation";

type AreaOption = {
  label: string;
  value: string;
  isFull: boolean;
  isNonCongested: boolean;
  isRegionAgnostic?: boolean;
};

type RequestDialogProps = {
  visible: boolean;
  onSubmitSuccess: () => void;
  onClose: () => void;
  defaultArea?: string;
  isAreaFixed?: boolean;
};

const REGION_AGNOSTIC_LABEL = "지역 무관";

const RESTRICTED_BIZ_KEYWORDS = [
  "여행",
  "관광",
  "식품",
  "운수",
  "대부",
  "환자",
  "대중문화",
  "부동산중개",
  "임대",
  "음식점",
];

const RequestDialog = ({
  visible,
  onSubmitSuccess,
  onClose,
  defaultArea,
  isAreaFixed = false,
}: RequestDialogProps) => {
  const [request, setRequest] = useRecoilState(requestState);
  const [, setPersistedRequest] = useLocalStorage("request", request);
  const resetRequest = useResetRecoilState(requestState);
  const requestService = useInjection<RequestService>(REQUEST_SERVICE);
  const keyword = useAtomValue(totalKeywordAtom);
  const [errors, setErrors] = useRecoilState(requestErrorState);
  const [next, setNext] = useState<boolean>(false);
  const [isAreaDropdownOpen, setIsAreaDropdownOpen] = useState(false);
  const [areaOptions, setAreaOptions] = useState<AreaOption[]>([]);
  const [isLoadingAreas, setIsLoadingAreas] = useState(false);
  const [hasAreaLoadFailed, setHasAreaLoadFailed] = useState(false);
  const [isNameFocused, setIsNameFocused] = useState(false);
  const [isPhoneFocused, setIsPhoneFocused] = useState(false);
  const [hasNameBlurred, setHasNameBlurred] = useState(false);
  const [hasPhoneBlurred, setHasPhoneBlurred] = useState(false);
  const loading = useLoading();
  const dropdownRef = useRef<HTMLDivElement>(null);

  const updateRequest = useCallback(
    (updater: (prev: RequestForm) => RequestForm) => {
      setRequest((prev) => {
        const nextRequest = updater(prev);
        setPersistedRequest(nextRequest);
        return nextRequest;
      });
    },
    [setPersistedRequest, setRequest]
  );

  const nameErrorMessage = useMemo(
    () => validateContactName(request.name ?? ""),
    [request.name]
  );
  const phoneErrorMessage = useMemo(
    () => validateContactPhone(request.phone ?? ""),
    [request.phone]
  );

  const shouldShowNameError =
    hasNameBlurred && !isNameFocused && Boolean(nameErrorMessage);
  const shouldShowPhoneError =
    hasPhoneBlurred && !isPhoneFocused && Boolean(phoneErrorMessage);

  const hasRestrictedBizKeyword = useMemo(() => {
    const bizType = request.bizType?.trim();
    if (!bizType) {
      return false;
    }

    return RESTRICTED_BIZ_KEYWORDS.some((keyword) =>
      bizType.includes(keyword)
    );
  }, [request.bizType]);

  const isSubmitDisabled = useMemo(() => {
    return (
      Boolean(nameErrorMessage) ||
      Boolean(phoneErrorMessage) ||
      !request.bizType?.trim() ||
      hasRestrictedBizKeyword ||
      !request.area?.trim() ||
      !request.isPrivacyPolicyChecked
    );
  }, [
    hasRestrictedBizKeyword,
    nameErrorMessage,
    phoneErrorMessage,
    request.area,
    request.bizType,
    request.isPrivacyPolicyChecked,
  ]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsAreaDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const sortedAreaOptions = useMemo(() => {
    const list = [...areaOptions];
    list.sort((a, b) => {
      if (a.isRegionAgnostic && !b.isRegionAgnostic) {
        return -1;
      }

      if (!a.isRegionAgnostic && b.isRegionAgnostic) {
        return 1;
      }

      if (a.isFull !== b.isFull) {
        return Number(Boolean(a.isFull)) - Number(Boolean(b.isFull));
      }

      return 0;
    });
    return list;
  }, [areaOptions]);

  useEffect(() => {
    let isMounted = true;

    const loadAreas = async () => {
      setIsLoadingAreas(true);
      const areas = await getAreaListFromAirtable();

      if (!isMounted) {
        return;
      }

      if (areas.length === 0) {
        setHasAreaLoadFailed(true);
      } else {
        setHasAreaLoadFailed(false);
      }

      const filteredAreas = areas.filter(
        (area) => area.areaName !== REGION_AGNOSTIC_LABEL
      );

      const nextOptions: AreaOption[] = filteredAreas.map((area) => ({
        label: area.areaName,
        value: area.areaName,
        isFull: isSoldOutState(area.state),
        isNonCongested: area.taxBenefit && area.areaName !== REGION_AGNOSTIC_LABEL,
      }));

      const hasRegionAgnosticOption = nextOptions.some(
        (option) => option.value === REGION_AGNOSTIC_LABEL
      );

      if (!hasRegionAgnosticOption) {
        nextOptions.push({
          label: REGION_AGNOSTIC_LABEL,
          value: REGION_AGNOSTIC_LABEL,
          isFull: false,
          isNonCongested: false,
          isRegionAgnostic: true,
        });
      }

      setAreaOptions(nextOptions);
      setIsLoadingAreas(false);
    };

    loadAreas();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (defaultArea && isAreaFixed && request.area !== defaultArea) {
      updateRequest((prev) => ({ ...prev, area: defaultArea }));
      setIsAreaDropdownOpen(false);
    }
  }, [defaultArea, isAreaFixed, request.area, updateRequest]);

  const sendRequest = useCallback(async () => {
    await requestService?.sendRequest(request, {
      first: keyword.final ? keyword.final : keyword.first,
      final: keyword.first,
    });
    window.localStorage.removeItem("request");
    resetRequest();
    setNext(true);
  }, [keyword.final, keyword.first, request, requestService, resetRequest]);

  const onSubmit = useCallback(
    async (e: any) => {
      e.preventDefault();
      setHasNameBlurred(true);
      setHasPhoneBlurred(true);

      const err = requestService.checkRequest(request);
      setErrors(err);

      if (!err.isValid()) {
        return;
      }
      loading.show();
      try {
        await sendRequest();
      } catch (e) {
        console.log(e);
        loading.hide();
        alert("접수 처리 중 오류가 발생했습니다. 다시 시도해 주세요.");
        return;
      }
      if ((window as any).fbq) {
        (window as any).fbq("track", "PageView");
      }
      if ((window as any).karrotPixel) {
        (window as any).karrotPixel.track("SubmitApplication");
      }
      loading.hide();
    },
    [loading, request, requestService, sendRequest, setErrors]
  );

  const onChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    const nextValue =
      name === "name" ? sanitizeContactNameInput(value) : value;

    updateRequest(
      (prev) => ({ ...prev, [name]: nextValue }) as RequestForm
    );
  };

  const handleAreaSelect = (option: AreaOption) => {
    if (isAreaFixed) {
      return;
    }
    updateRequest((prev) => ({ ...prev, area: option.label }));
    setIsAreaDropdownOpen(false);
  };

  const handlePhoneChange = (event: ChangeEvent<HTMLInputElement>) => {
    const inputElement = event.currentTarget;
    const rawValue = inputElement.value;
    const nextCaret = getMobilePhoneCaretPosition(
      rawValue,
      inputElement.selectionStart
    );

    updateRequest((prev) => ({ ...prev, phone: rawValue }));

    window.requestAnimationFrame(() => {
      if (document.activeElement !== inputElement) {
        return;
      }

      inputElement.setSelectionRange(nextCaret, nextCaret);
    });
  };

  return (
    <Dialog visible={visible} onClose={onClose}>
      <div className="dialog-inner">
        {!next && (
          <div className="close-button" onClick={onClose}>
            <CloseIcon />
          </div>
        )}
        <div className={`dialog-content ${next ? "next" : ""}`}>
          <div
            className="request-card"
            style={{
              justifyContent: next ? "center" : "flex-start",
            }}
          >
            {next ? (
              <>
                <h1 className="card-title">
                  감사합니다.
                  <br />
                  문의가 완료되었습니다.
                </h1>
                <p>오피스 매니저가 신속히 연락드리도록 하곘습니다.</p>
                <div className="box">
                  <strong>상담 가능시간 :</strong>
                  평일 10시 ~ 18시
                </div>
                <div className="go-main" onClick={onSubmitSuccess}>
                  메인으로
                </div>
              </>
            ) : (
              <>
                <h1 className="card-title">상담신청</h1>
                <form className="input-wrapper">
                  <div className="input">
                    <input
                      type="text"
                      placeholder="성함"
                      name="name"
                      value={request.name}
                      onChange={onChange}
                      onFocus={() => setIsNameFocused(true)}
                      onBlur={() => {
                        setIsNameFocused(false);
                        setHasNameBlurred(true);
                      }}
                    />
                  </div>
                  {shouldShowNameError ? (
                    <p className="error">
                      {nameErrorMessage ??
                        CONTACT_VALIDATION_ERROR_MESSAGES.name}
                    </p>
                  ) : (
                    false
                  )}
                  <div className="input">
                    <input
                      name="phone"
                      type="text"
                      placeholder="휴대폰 번호"
                      onChange={handlePhoneChange}
                      value={request.phone}
                      onFocus={() => setIsPhoneFocused(true)}
                      onBlur={() => {
                        setIsPhoneFocused(false);
                        setHasPhoneBlurred(true);
                      }}
                      maxLength={13}
                    />
                  </div>
                  {shouldShowPhoneError ? (
                    <p className="error">
                      {phoneErrorMessage ??
                        CONTACT_VALIDATION_ERROR_MESSAGES.phone}
                    </p>
                  ) : (
                    false
                  )}
                  <div className="input">
                    <input
                      type="text"
                      placeholder="업종"
                      name="bizType"
                      value={request.bizType ?? ""}
                      onChange={onChange}
                    />
                  </div>
                  {hasRestrictedBizKeyword ? (
                    <p className="error">
                      해당 업종은 비상주사무실 주소로 사업자등록이 불가합니다.
                    </p>
                  ) : errors.isBizTypeError ? (
                    <p className="error">필수 입력사항입니다</p>
                  ) : (
                    false
                  )}
                  <div className="input dropdown" ref={dropdownRef}>
                    <button
                      type="button"
                      className={`dropdown-display ${
                        request.area ? "selected" : ""
                      }`}
                      onClick={() => {
                        if (isAreaFixed) {
                          return;
                        }
                        setIsAreaDropdownOpen((prev) => !prev);
                      }}
                      disabled={isAreaFixed}
                      aria-disabled={isAreaFixed}
                    >
                      {request.area
                        ? request.area
                        : hasAreaLoadFailed
                          ? "지점 정보를 불러오지 못했습니다"
                          : isLoadingAreas
                            ? "지점을 불러오는 중입니다"
                            : "지점"}
                      {!isAreaFixed && <span className="caret" />}
                    </button>
                    {errors.isAreaError ? (
                      <p className="error">필수 입력사항입니다</p>
                    ) : (
                      false
                    )}
                    {!isAreaFixed && isAreaDropdownOpen && (
                      <div className="dropdown-options">
                        {isLoadingAreas ? (
                          <div className="dropdown-empty">
                            선택 가능한 지점을 불러오는 중입니다.
                          </div>
                        ) : sortedAreaOptions.length === 0 ? (
                          <div className="dropdown-empty">
                            표시할 지점 정보가 없습니다.
                          </div>
                        ) : (
                          sortedAreaOptions.map((option) => (
                            <button
                              key={option.value}
                              type="button"
                              className={`dropdown-option ${
                                option.isFull ? "disabled" : ""
                              } ${
                                request.area === option.label ? "active" : ""
                              }`}
                              disabled={option.isFull}
                              onClick={() => handleAreaSelect(option)}
                            >
                              {option.label}
                              {option.isFull ? (
                                <span className="status">만실</span>
                              ) : option.isNonCongested ? (
                                <span className="status non-congested">
                                  비과밀
                                </span>
                              ) : null}
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                  <textarea
                    placeholder="문의사항 (선택)"
                    rows={4}
                    name="comment"
                    onChange={onChange}
                    value={request.comment ?? ""}
                  />
                  <div className="check-input">
                    <input
                      type="checkbox"
                      id="cb"
                      checked={request.isPrivacyPolicyChecked}
                      onChange={() => {
                        updateRequest((prev) => ({
                          ...prev,
                          isPrivacyPolicyChecked: !prev.isPrivacyPolicyChecked,
                        }));
                      }}
                    />
                    <label htmlFor="cb" className="checkbox" />
                    <Link
                      to="/terms"
                      target="_blank"
                      style={{
                        color: errors.isPrivacyPolicyError
                          ? "#EB5757"
                          : "#9198a4",
                      }}
                    >
                      (필수) <span className="underline">개인정보취급방침</span>
                      에 동의합니다
                    </Link>
                  </div>
                  <input
                    type="submit"
                    onClick={onSubmit}
                    value="신청하기"
                    disabled={isSubmitDisabled}
                    className={isSubmitDisabled ? "disabled" : ""}
                  />
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default RequestDialog;
